import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';

import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import { DialogFooter } from '#/modules/commonComponents/dialog/DialogPrimitives';
import { dialog3xlMaxWidthClassName } from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import TagInput from '#/modules/commonComponents/form/TagInput';
import {
  joinAccessories,
  parseAccessories,
} from '#/modules/crmMgmt/aftersales/repairRequest/helper/accessoriesTags';

const CORRECTION_FORM_ID = 'repair-request-correction-form';
const GRID_CLASS = 'grid grid-cols-1 gap-x-3 gap-y-3 sm:grid-cols-2';
const FULL_SPAN_CLASS = 'sm:col-span-2';
const LINE_CARD_CLASS = cn(
  'rounded-md border border-gray-200 bg-gray-50 px-3 py-5',
  '[--field-bg:var(--color-gray-50)]',
);

function toFormValues(detail) {
  return {
    reason: '',
    header: {
      repairRequestDate: detail?.repairRequestDate ?? '',
      receivedDate: detail?.receivedDate ?? '',
      customerName: detail?.customerName ?? '',
      contactName: detail?.contactName ?? '',
      phone: detail?.phone ?? '',
      fax: detail?.fax ?? '',
      taxId: detail?.taxId ?? '',
      address: detail?.address ?? '',
      remark: detail?.remark ?? '',
    },
    lines: (detail?.lines ?? []).map((line) => ({
      id: line.id,
      serialNo: line.serialNo ?? '',
      problemDescription: line.problemDescription ?? '',
      accessories: line.accessories ?? '',
      remark: line.remark ?? '',
      version: line.version ?? '',
      salesDate: line.salesDate ?? '',
      warrantyExpiryDate: line.warrantyExpiryDate ?? '',
    })),
  };
}

/**
 * 成立後更正表頭與品項（不含 SN、增減品項）。只送有變動的欄位；原因必填。
 * 開啟時才掛載，表單值取當下 detail。
 */
const CorrectionForm = ({ detail, onSubmit }) => {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: toFormValues(detail) });
  // 品項數固定（增減走另一支 route），直接用 detail 的品項排版
  const lines = detail?.lines ?? [];

  return (
    <form
      id={CORRECTION_FORM_ID}
      className="space-y-5 pt-4"
      onSubmit={handleSubmit(onSubmit)}
    >
      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-700">表頭</h3>
        <div className={GRID_CLASS}>
          <FloatingDateInput
            id="header.repairRequestDate"
            label="報修日"
            control={control}
            errors={errors}
            placeholder="西元年/月/日"
          />
          <FloatingDateInput
            id="header.receivedDate"
            label="收件日"
            control={control}
            errors={errors}
            placeholder="西元年/月/日"
          />
          <FloatingInput id="header.customerName" type="text" label="客戶名稱" register={register} />
          <FloatingInput id="header.contactName" type="text" label="聯絡人" register={register} />
          <FloatingInput id="header.phone" type="text" label="電話" register={register} />
          <FloatingInput id="header.fax" type="text" label="傳真" register={register} />
          <FloatingInput id="header.taxId" type="text" label="統一編號" register={register} />
          <FloatingInput id="header.address" type="text" label="地址" register={register} />
          <FloatingTextarea
            id="header.remark"
            label="備註"
            register={register}
            rows={2}
            containerClassName={FULL_SPAN_CLASS}
          />
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-700">品項</h3>
        <p className="text-xs text-gray-500">
          序號請用品項列的「更正序號」；已完成或拒修的品項送出後會被拒絕。
        </p>
        <ul className="space-y-3">
          {lines.map((line, index) => {
            const accessoriesId = `lines.${index}.accessories`;
            return (
              <li key={line.id} className={LINE_CARD_CLASS}>
                <p className="mb-4 text-sm font-medium text-gray-700">
                  <span className="tabular-nums">{`項目# ${index + 1}`}</span>
                  <span className="ml-2 font-normal text-gray-500">{line.serialNo || '—'}</span>
                </p>
                <div className={GRID_CLASS}>
                  <FloatingTextarea
                    id={`lines.${index}.problemDescription`}
                    label="問題描述"
                    register={register}
                    rows={2}
                    containerClassName={FULL_SPAN_CLASS}
                  />
                  <div className={cn('min-w-0', FULL_SPAN_CLASS)}>
                    <Controller
                      name={accessoriesId}
                      control={control}
                      render={({ field }) => (
                        <TagInput
                          id={accessoriesId}
                          label="配件（輸入後按 Enter 新增，可多個）"
                          value={parseAccessories(field.value)}
                          onChange={(tags) => field.onChange(joinAccessories(tags))}
                          onBlur={field.onBlur}
                        />
                      )}
                    />
                  </div>
                  <FloatingInput
                    id={`lines.${index}.version`}
                    type="text"
                    label="版本"
                    register={register}
                  />
                  <div />
                  <FloatingDateInput
                    id={`lines.${index}.salesDate`}
                    label="銷售日"
                    control={control}
                    errors={errors}
                    placeholder="西元年/月/日"
                  />
                  <FloatingDateInput
                    id={`lines.${index}.warrantyExpiryDate`}
                    label="保固迄日"
                    control={control}
                    errors={errors}
                    placeholder="西元年/月/日"
                  />
                  <FloatingTextarea
                    id={`lines.${index}.remark`}
                    label="明細備註"
                    register={register}
                    rows={2}
                    containerClassName={FULL_SPAN_CLASS}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      <FloatingTextarea
        id="reason"
        label="更正原因（必填）"
        register={register}
        rows={2}
      />
    </form>
  );
};

CorrectionForm.propTypes = {
  detail: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

const RepairRequestCorrectionDialog = ({
  open,
  onOpenChange,
  detail,
  isPending = false,
  onSubmit,
}) => (
  <AppDialog
    open={open}
    onOpenChange={onOpenChange}
    title="更正報修單"
    description="更正後保留原單號與查詢碼，內部會記錄修改前後值與原因；客戶只看到更正後資料。"
    maxWidthClassName={dialog3xlMaxWidthClassName}
    closeOnOutsideClick={false}
    footer={(
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          取消
        </Button>
        <Button type="submit" form={CORRECTION_FORM_ID} disabled={isPending}>
          {isPending ? '更正中…' : '送出更正'}
        </Button>
      </DialogFooter>
    )}
  >
    {open ? (
      <CorrectionForm
        key={detail?.record?.updatedAt ?? ''}
        detail={detail}
        onSubmit={onSubmit}
      />
    ) : null}
  </AppDialog>
);

RepairRequestCorrectionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  detail: PropTypes.object.isRequired,
  isPending: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
};

export default RepairRequestCorrectionDialog;
